import React, { useState, useEffect, useId } from 'react';
import { 
  ShieldCheck, X, Check, Sliders, ExternalLink, 
  Lock, BarChart3, Settings2, Megaphone, ShieldAlert,
  ChevronDown, ChevronUp, Info, HelpCircle
} from 'lucide-react';
import { SponsoredService } from '../services/sponsoredService';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { CookieConsentService, CookiePreferences } from '../services/cookieConsentService';

export const CookieConsent: React.FC = () => {
  const tokens = useThemeTokens();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [config, setConfig] = useState(SponsoredService.getConfig());
  
  // Local state for granular preferences within modal
  const [preferences, setPreferences] = useState<CookiePreferences>(() => CookieConsentService.getPreferences());
  const [expandedCategory, setExpandedCategory] = useState<string | null>('necessary');

  const bannerId = useId();
  const modalId = useId();

  useEffect(() => {
    const handleConfigUpdate = () => setConfig(SponsoredService.getConfig());
    window.addEventListener('sponsored-config-updated', handleConfigUpdate);

    // Check if user has already decided
    const hasDecided = CookieConsentService.hasUserDecided();
    if (!hasDecided && config.cookieConsent.isEnabled) {
      // Delay slightly for smooth page load UX
      const timer = setTimeout(() => setIsVisible(true), 1200);
      return () => clearTimeout(timer);
    }

    return () => window.removeEventListener('sponsored-config-updated', handleConfigUpdate);
  }, [config.cookieConsent.isEnabled]);

  // Support opening preferences from anywhere (e.g. footer link or policy page)
  useEffect(() => {
    const handleOpenPreferences = () => {
      setPreferences(CookieConsentService.getPreferences());
      setShowPreferencesModal(true);
    };

    window.addEventListener('open-cookie-preferences', handleOpenPreferences);
    return () => window.removeEventListener('open-cookie-preferences', handleOpenPreferences);
  }, []);

  const handleAcceptAll = () => {
    const updated = CookieConsentService.acceptAll();
    setPreferences(updated);
    setIsVisible(false);
    setShowPreferencesModal(false);
  };

  const handleRejectNonEssential = () => {
    const updated = CookieConsentService.rejectNonEssential();
    setPreferences(updated);
    setIsVisible(false);
    setShowPreferencesModal(false);
  };

  const handleSaveCustomPreferences = () => {
    const updated = CookieConsentService.savePreferences({
      status: 'customized',
      analytics: preferences.analytics,
      functional: preferences.functional,
      marketing: preferences.marketing,
      doNotSell: preferences.doNotSell
    });
    setPreferences(updated);
    setIsVisible(false);
    setShowPreferencesModal(false);
  };

  const toggleCategory = (cat: 'analytics' | 'functional' | 'marketing') => {
    setPreferences(prev => {
      const nextVal = !prev[cat];
      return {
        ...prev,
        [cat]: nextVal,
        // If disabling marketing, keep doNotSell in sync for CCPA
        doNotSell: cat === 'marketing' ? !nextVal : prev.doNotSell
      };
    });
  };

  const toggleDoNotSell = () => {
    setPreferences(prev => {
      const nextDoNotSell = !prev.doNotSell;
      return {
        ...prev,
        doNotSell: nextDoNotSell,
        marketing: nextDoNotSell ? false : prev.marketing
      };
    });
  };

  // If banner is not enabled in admin config and modal is not opened, render nothing
  if ((!isVisible && !showPreferencesModal) || (!config.cookieConsent.isEnabled && !showPreferencesModal)) {
    return null;
  }

  return (
    <>
      {/* 1. NATIVE FLOATING CONSENT BANNER (Bottom Bar) */}
      {isVisible && !showPreferencesModal && (
        <aside 
          id={bannerId}
          role="region"
          aria-label="Privacy & Cookie Preferences"
          className="fixed bottom-3 sm:bottom-5 left-3 sm:left-6 right-3 sm:right-6 z-[250] animate-slide-up max-w-5xl mx-auto"
        >
          <div className={`p-4 sm:p-5 md:p-6 rounded-2xl md:rounded-3xl shadow-2xl border backdrop-blur-xl transition-all duration-300 ${tokens.cardBg} ${tokens.accentBorder}`}>
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
              
              {/* Left Column: Branding, Title, Message */}
              <div className="space-y-2 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 ${tokens.iconBg}`}>
                    <ShieldCheck size={16} />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    Diamond School of Nursing · Privacy & Compliance
                  </span>
                  <span className={`px-2 py-0.5 text-[9px] font-bold rounded-md ${tokens.badge}`}>
                    GDPR & CCPA Compliant
                  </span>
                </div>

                <h3 className={`text-base sm:text-lg font-bold tracking-tight ${tokens.headerFont}`}>
                  {config.cookieConsent.title || 'Your Privacy & Cookie Choices'}
                </h3>

                <p className="text-xs text-slate-600 dark:text-slate-300 font-medium leading-relaxed max-w-3xl">
                  {config.cookieConsent.message || 
                    'We use strictly necessary cookies to power student authentication, plus optional performance, functional, and study-partner cookies to deliver personalized state-board prep drills. You are in full control of your data choices.'}
                </p>

                {/* Quiet text links */}
                <div className="flex items-center gap-3 text-[11px] font-bold pt-1">
                  <a 
                    href="/cookie-consent" 
                    className={`${tokens.accentText} hover:underline flex items-center gap-1`}
                  >
                    <span>Cookie Policy & Technical Inventory</span>
                    <ExternalLink size={11} />
                  </a>
                  <span className="text-slate-300 dark:text-slate-700">·</span>
                  <a 
                    href={config.cookieConsent.policyUrl || 'https://www.diamondnursingschool.com/compliance/privacy-policy'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>

              {/* Right Column: Interactive Action Controls */}
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 w-full lg:w-auto shrink-0 pt-1 lg:pt-0">
                <button
                  type="button"
                  onClick={() => setShowPreferencesModal(true)}
                  className="px-4 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-1.5 active:scale-95 flex-1 sm:flex-none cursor-pointer"
                  title="Customize granular cookie preferences"
                >
                  <Sliders size={13} className={tokens.accentText} />
                  <span>Customize</span>
                </button>

                <button
                  type="button"
                  onClick={handleRejectNonEssential}
                  className={`px-4 py-3 ${tokens.secondaryBtn} rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-1.5 active:scale-95 flex-1 sm:flex-none cursor-pointer`}
                >
                  <span>{config.cookieConsent.declineButtonText || 'Reject Non-Essential'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className={`px-6 py-3 ${tokens.primaryBtn} rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-1.5 active:scale-95 w-full sm:w-auto cursor-pointer shadow-md`}
                >
                  <Check size={14} />
                  <span>{config.cookieConsent.acceptButtonText || 'Accept All'}</span>
                </button>
              </div>

            </div>
          </div>
        </aside>
      )}

      {/* 2. GRANULAR PREFERENCES MODAL (GDPR, ePrivacy, CCPA / CPRA) */}
      {showPreferencesModal && (
        <div 
          id={modalId}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-preferences-title"
          className="fixed inset-0 z-[300] bg-slate-950/75 backdrop-blur-md flex items-center justify-center p-3 sm:p-5 overflow-y-auto animate-fade-in"
        >
          <div className={`relative w-full max-w-2xl rounded-3xl shadow-2xl border my-auto max-h-[92vh] flex flex-col overflow-hidden animate-scale-up ${tokens.cardBg} ${tokens.accentBorder}`}>
            
            {/* Modal Header */}
            <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-4 shrink-0 bg-slate-50/70 dark:bg-slate-900/70">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-white shrink-0 ${tokens.iconBg}`}>
                    <Sliders size={16} />
                  </div>
                  <div>
                    <h2 id="cookie-preferences-title" className={`text-lg sm:text-xl font-black tracking-tight ${tokens.headerFont}`}>
                      Cookie & Privacy Preference Center
                    </h2>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Diamond School of Nursing · Student Data Sovereignty
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowPreferencesModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                aria-label="Close preferences"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body - Scrollable Category Cards */}
            <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1 text-slate-800 dark:text-slate-200">
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                When you access the Diamond School of Nursing Study Hub, we store and retrieve data on your browser. 
                This information may relate to your testing state, audio speeds, or device analytics. You can adjust your consent categories below.
              </p>

              {/* CCPA / CPRA Notice & Quick Toggle */}
              <div className="p-3.5 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 flex items-start justify-between gap-3">
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-1.5 font-bold text-amber-900 dark:text-amber-200">
                    <ShieldAlert size={14} className="text-amber-600 dark:text-amber-400 shrink-0" />
                    <span>California Consumer Privacy (CCPA / CPRA) Opt-Out</span>
                  </div>
                  <p className="text-[11px] text-amber-800 dark:text-amber-300/90 leading-tight">
                    Do Not Sell or Share My Personal Information. Enabling this disables advertising cookies and cross-context behavioral tracking.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleDoNotSell}
                  role="switch"
                  aria-checked={preferences.doNotSell}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.doNotSell ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.doNotSell ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Category 1: Strictly Necessary (Always Active) */}
              <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                      <Lock size={16} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        Strictly Necessary Cookies
                        <span className="text-[9px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/60 px-2 py-0.5 rounded-md">
                          Always Active
                        </span>
                      </h4>
                      <p className="text-[11px] text-slate-500">Authentication, CSRF security, exam integrity & session state</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandedCategory(expandedCategory === 'necessary' ? null : 'necessary')}
                    className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
                    aria-label="Toggle details"
                  >
                    {expandedCategory === 'necessary' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>

                {expandedCategory === 'necessary' && (
                  <div className="text-[11px] text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800 pt-2.5 space-y-2 animate-fade-in leading-relaxed">
                    <p>
                      These cookies are strictly required to operate the application. They authenticate your Wix Member profile, enable session hand-offs, maintain CSRF token security, and store this consent record. They cannot be turned off.
                    </p>
                    <div className="font-mono text-[10px] text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
                      Active: <strong>wix_auth_token</strong>, <strong>dns_session_token</strong>, <strong>cna_cookie_consent</strong>, <strong>active_cert_id</strong>
                    </div>
                  </div>
                )}
              </div>

              {/* Category 2: Performance & Analytics */}
              <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center shrink-0">
                      <BarChart3 size={16} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                        Performance & Diagnostic Analytics
                      </h4>
                      <p className="text-[11px] text-slate-500">Google Analytics 4 & quiz load latency monitoring</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleCategory('analytics')}
                      role="switch"
                      aria-checked={preferences.analytics}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.analytics ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.analytics ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpandedCategory(expandedCategory === 'analytics' ? null : 'analytics')}
                      className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
                      aria-label="Toggle details"
                    >
                      {expandedCategory === 'analytics' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {expandedCategory === 'analytics' && (
                  <div className="text-[11px] text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800 pt-2.5 space-y-2 animate-fade-in leading-relaxed">
                    <p>
                      These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our Study Hub. They help us know which clinical domains are most popular and track diagnostic load times.
                    </p>
                    <div className="font-mono text-[10px] text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
                      Active: <strong>_ga</strong>, <strong>_ga_*</strong>, <strong>cna_speed_metric</strong>
                    </div>
                  </div>
                )}
              </div>

              {/* Category 3: Functional & Learning Personalization */}
              <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                      <Settings2 size={16} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                        Functional & Learning Preferences
                      </h4>
                      <p className="text-[11px] text-slate-500">State testing jurisdiction, audio speed & theme memory</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleCategory('functional')}
                      role="switch"
                      aria-checked={preferences.functional}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.functional ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.functional ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpandedCategory(expandedCategory === 'functional' ? null : 'functional')}
                      className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
                      aria-label="Toggle details"
                    >
                      {expandedCategory === 'functional' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {expandedCategory === 'functional' && (
                  <div className="text-[11px] text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800 pt-2.5 space-y-2 animate-fade-in leading-relaxed">
                    <p>
                      Enables enhanced functionality and personalization, such as remembering your specific state testing board (e.g. California vs Florida), active interface theme, font accessibility sizing, and audio lecture rates.
                    </p>
                    <div className="font-mono text-[10px] text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
                      Active: <strong>dns_selected_state</strong>, <strong>data-theme</strong>, <strong>audio_playback_rate</strong>, <strong>fontSize</strong>
                    </div>
                  </div>
                )}
              </div>

              {/* Category 4: Marketing & Clinical Advertising */}
              <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                      <Megaphone size={16} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                        Advertising & Educational Partners
                      </h4>
                      <p className="text-[11px] text-slate-500">Google AdSense, Meta Pixel & nursing gear promos</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleCategory('marketing')}
                      role="switch"
                      aria-checked={preferences.marketing}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${preferences.marketing ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                    >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${preferences.marketing ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpandedCategory(expandedCategory === 'marketing' ? null : 'marketing')}
                      className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
                      aria-label="Toggle details"
                    >
                      {expandedCategory === 'marketing' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {expandedCategory === 'marketing' && (
                  <div className="text-[11px] text-slate-600 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800 pt-2.5 space-y-2 animate-fade-in leading-relaxed">
                    <p>
                      Used by our authorized education advertising partners (Google AdSense ca-pub-6693032236212368 and Meta) to deliver nursing career listings, antimicrobial scrub discounts, and course announcements.
                    </p>
                    <div className="font-mono text-[10px] text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-950 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
                      Active: <strong>_fbp</strong>, <strong>__gads</strong>, <strong>__gpi</strong>
                    </div>
                  </div>
                )}
              </div>

            </div>

            {/* Modal Footer Controls */}
            <div className="p-4 sm:p-5 border-t border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/70 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
              <a 
                href="/cookie-consent" 
                className={`text-xs font-bold ${tokens.accentText} hover:underline flex items-center gap-1 order-2 sm:order-1`}
              >
                <span>Read Full Technical Cookie Policy</span>
                <ExternalLink size={12} />
              </a>

              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 w-full sm:w-auto order-1 sm:order-2">
                <button
                  type="button"
                  onClick={handleRejectNonEssential}
                  className={`px-4 py-2.5 ${tokens.secondaryBtn} rounded-xl font-bold text-xs transition-all flex-1 sm:flex-none cursor-pointer`}
                >
                  Reject All
                </button>
                <button
                  type="button"
                  onClick={handleSaveCustomPreferences}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white dark:bg-slate-700 dark:hover:bg-slate-600 rounded-xl font-bold text-xs transition-all flex-1 sm:flex-none cursor-pointer"
                >
                  Save Choices
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className={`px-5 py-2.5 ${tokens.primaryBtn} rounded-xl font-bold text-xs transition-all flex-1 sm:flex-none cursor-pointer shadow-md`}
                >
                  Accept All
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
};
